import React, { Component, ReactNode } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { logger } from '../../utils/logger';
import { trackAppError } from '../../utils/appInsights';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    logger.error('React component error boundary triggered', error, {
      componentStack: errorInfo.componentStack,
      errorBoundary: this.constructor.name
    });
    // Report to Application Insights (no-op when telemetry is disabled). The
    // component stack can contain rendered content, so it is PHI-scrubbed by the
    // telemetry initializer before send.
    trackAppError(error, {
      componentStack: errorInfo.componentStack ?? undefined,
      errorBoundary: this.constructor.name,
    });
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-[400px] flex items-center justify-center" role="alert" aria-live="assertive">
          <div className="text-center p-6 max-w-md">
            <ExclamationTriangleIcon className="h-12 w-12 text-clinic-accent mx-auto mb-4" aria-hidden="true" />
            <h2 className="text-lg font-semibold text-clinic-dark mb-2" id="error-title">
              Something went wrong
            </h2>
            <p className="text-sm text-clinic-muted mb-4" id="error-description">
              An unexpected error occurred while loading the availability calendar.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-clinic-primary text-white rounded-md hover:bg-clinic-primary focus:outline-none focus:ring-2 focus:ring-clinic-primary focus:ring-offset-2 transition-colors"
              aria-describedby="error-title error-description"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;